import React, { Component } from 'react';
import { inject, observer } from 'mobx-react';

import { Card, Container, Flex, FlexColumn, Heading, Icon, LinkButton, Tag, Text } from 'core/components';
import styled from 'styled-components';
import { Field, Form, Switch } from 'core/form';
import { PiSparkleFill } from 'react-icons/pi';

export const TeaserCard = styled(Card)`
  &.bp4-card {
    flex: 1;
    padding: 24px;
    min-height: 252px;
    display: flex;
    flex-direction: column;
    border: ${({ theme }) => theme.borders.thinLight};
    box-shadow: 0px 2px 10px 0px rgba(0, 0, 0, 0.07);
    position: relative;
    overflow: hidden;

    .bp4-button {
      align-self: flex-start;
    }

    .bp4-control {
      display: inline-flex;
      align-items: center;
    }

    .bp4-control.bp4-switch .bp4-control-indicator {
      width: 48px;
      height: 24px;
      background: rgba(0, 0, 0, 0.35);
      border: 1px solid rgba(255, 255, 255, 0.25);

      &::before {
        width: 18px;
        height: 18px;
        position: relative;
        top: 0px;
      }
    }

    .bp4-control.bp4-switch input:checked ~ .bp4-control-indicator {
      background: ${({ theme }) => theme.colors.success} !important;

      &::before {
        left: calc(100% - 24px);
      }
    }

    ${({ fancyBorder, theme }) =>
      fancyBorder &&
      `
      background: ${
        theme.name === 'light'
          ? 'radial-gradient(335.21% 135.09% at -9.24% 9.26%, #FCE5D2 0%, rgba(244, 205, 215, 0.60) 48.42%, rgba(215, 238, 254, 0.60) 84.55%, #DDDFF9 100%)'
          : 'linear-gradient(90deg, #282828 0%, #512E21 71.11%, #321337 100%)'
      };
      background-size: 100% 100%;
      border: none;
      outline: 1px solid rgba(0, 0, 0, 0.35);
      position: relative; /* Ensure positioning for the pseudo-element */
      border-radius: 12px;
      animation: gradientAnimation 5s ease infinite;
    `}
  }

  /* Animation keyframes for the rainbow gradient */
  @keyframes gradientAnimation {
    0% {
      background-position: 0% 50%;
    }
    50% {
      background-position: 200% 50%;
    }
    100% {
      background-position: 0% 50%;
    }
  }
`;

const Teaser = ({ title, description, icon, link, enableKentikAi, comingSoon }) => {
  let color;
  let opacity = 1;

  if (enableKentikAi) {
    color = 'success';

    if (title === 'AI Enhanced Insights') {
      color = 'muted';
      opacity = 0.5;
    }
  } else {
    color = 'orange';
  }

  return (
    <TeaserCard>
      <FlexColumn gap={2} flex={1}>
        <Icon icon={enableKentikAi ? 'tick-circle' : icon} color={color} size={32} opacity={opacity} />

        <Heading level={comingSoon ? 5 : 4}>{title}</Heading>
        <Text as="div" flex={1} muted>
          {description}
        </Text>
        {!comingSoon && link && (
          <LinkButton to={link} blank>
            Learn more
          </LinkButton>
        )}
        {comingSoon && (
          <Tag large minimal round as="div" style={{ alignSelf: 'flex-start' }}>
            Coming soon!
          </Tag>
        )}
      </FlexColumn>
    </TeaserCard>
  );
};

const fields = {
  enableKentikAi: {
    defaultValue: false
  }
};

const options = { name: 'enableKentikAi' };

@inject('$companySettings', '$auth')
@Form({ fields, options })
@observer
class AIOverview extends Component {
  componentDidMount() {
    const { form, $companySettings } = this.props;
    form.setValue('enableKentikAi', $companySettings.enableKentikAi);
  }

  handleToggle = ({ value }) => {
    const { $companySettings, form } = this.props;

    $companySettings.setEnableKentikAi(value).then(() => {
      form.setValue('enableKentikAi', value);
    });
  };

  render() {
    const { form, $auth } = this.props;
    const enableKentikAi = form.getValue('enableKentikAi');

    const hasJourneysV2 = $auth.hasPermission('kaizen', { overrideForSudo: false });

    return (
      <Container maxWidth={850} p={4}>
        <TeaserCard style={{ minHeight: 'auto' }} mb={4} fancyBorder>
          <Flex flexDirection="column" gap="4px">
            <Field
              onChange={this.handleToggle}
              disabled={$auth.isPreciselyAdministrator}
              name="enableKentikAi"
              m={0}
              large
            >
              <Switch
                switchLabel={
                  <Flex flexDirection="column" alignItems="flex-start" gap="6px" ml={1}>
                    <Heading level={5} m={0}>
                      Enable Kentik AI
                    </Heading>
                    <Text muted fontWeight="medium" minimal>
                      Unlock powerful Al-driven features that simplify network monitoring, troubleshooting, and data
                      analysis.
                    </Text>
                  </Flex>
                }
              />
            </Field>
          </Flex>
        </TeaserCard>

        {!enableKentikAi && (
          <Heading level={3} textAlign="center" mb={3}>
            Enable Kentik AI to get access to the following features
          </Heading>
        )}

        <Flex gap={4} alignItems="flex-start" mb={4}>
          <Teaser
            enableKentikAi={enableKentikAi}
            icon="database"
            title="Query Assistant"
            description="Create queries by asking natural language questions about your network telemetry and infrastructure."
            link="https://kb.kentik.com/docs/query-assistant"
          />
          {hasJourneysV2 && (
            <Teaser
              enableKentikAi={enableKentikAi}
              icon={PiSparkleFill}
              title="AI Advisor"
              description="Accelerate troubleshooting and analysis with conversational AI contextually aware of your network."
              link="https://kb.kentik.com/docs/ai-advisor"
            />
          )}
        </Flex>

        <Flex gap={4} alignItems="flex-start">
          <Teaser
            title="Cause Analysis"
            icon="clean"
            description="Find the most contributing factors for sudden traffic changes, spikes, or drops."
            enableKentikAi={enableKentikAi}
          />
          <Teaser
            title="Summaries"
            icon="search-text"
            description="See simple breakdowns of complex network issues."
            enableKentikAi={enableKentikAi}
          />
          <Teaser
            title="AI Enhanced Insights"
            icon={enableKentikAi ? 'tick-circle' : 'star'}
            description="Get enhanced Kentik Insights with new AI-driven analysis."
            enableKentikAi={enableKentikAi}
          />
        </Flex>
      </Container>
    );
  }
}

export default AIOverview;
